import { useOnPageLoad } from '@assets/hooks/useOnPageLoad';
import {
    UICategoryFilter,
    useClientSettingStore,
    useSetPageTitle,
    useStore,
    useStoreUI
} from '@assets/model/Store';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import { Fragment, useEffect, useState } from 'react';
import {
    Highlighter,
    Menu,
    MenuItem,
    Typeahead
} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Flex } from '../../UI/Flex/Flex';
import * as style from './DealSearchHeader.module.less';

export const DealSearchHeader = observer(() => {
    const store = useStore();
    const ui = useStoreUI();
    useSetPageTitle('Case Database');

    const [multiSelectionsIndustry, setMultiSelectionsIndustry] = useState([]);
    const [textSearch, setTextSearch] = useState(ui.dealPageNameSearch);
    const [sortSelection, setSortSelection] = useState([
        {
            label:
                ui.dealPageSortOrder === 'newest'
                    ? 'Newest first'
                    : 'Oldest first',
            value: ui.dealPageSortOrder
        }
    ]);

    const setTextSearchDebounce = debounce((text: string) => {
        ui.dealPageNameSearch = text;
    }, 300);

    useOnPageLoad('DB_PAGE', () => {
        const ui = useStoreUI();
        ui.resetHome();
        resetFilters();
    });

    const resetFilters = () => {
        setMultiSelectionsIndustry([]);
        setTextSearch('');
        setSortSelection([{ label: 'Newest first', value: 'newest' }]);
        ui.dealPageNameSearch = '';
        ui.dealPageIndustrySearch = [];
        ui.dealPageSortOrder = 'newest';
    };

    const updateSelectionIndustry = (selected: []) => {
        var updated = [];
        selected.slice(0, 3).forEach((selection: any) => {
            if (!updated.find((item: any) => item.value == selection.value)) {
                updated.push({
                    label: selection.label,
                    value: selection.value
                });
            }
        });
        setMultiSelectionsIndustry(updated);
    };

    const updateSortSelection = (selected: any[]) => {
        if (selected && selected.length > 0) {
            setSortSelection(selected);
            ui.dealPageSortOrder = selected[0]?.value || 'newest';
        }
    };

    useEffect(() => {
        const filter: UICategoryFilter[] = [];
        multiSelectionsIndustry.forEach(selection => {
            const [type, value] = selection.value.split(':');
            filter.push({
                type: type == 'CAT' ? 'category' : 'sub_category',
                value: value
            });
        });

        ui.dealPageIndustrySearch = filter;
    }, [multiSelectionsIndustry]);

    // Calculate filtered deals count
    const getFilteredDealsCount = () => {
        if (!store.isLoaded) return 0;

        const dealKeys = Object.keys(store.reportDataDB);
        const filtered = dealKeys.filter(deal => {
            const e = store.reportDataDB[deal];
            if (e.status != 'completed') {
                return false;
            }
            const filter_settings =
                useClientSettingStore().filterCompaniesForDemo;
            if (
                useClientSettingStore().isDemoFilterOn &&
                filter_settings != null
            ) {
                if (filter_settings[deal] !== true) {
                    return false;
                }
            }

            const dataAccess = store.createDataAccess(deal);
            const companyName = dataAccess.companyName;
            const matchCompanyName =
                companyName
                    .toLowerCase()
                    .includes(ui.dealPageNameSearch.toLowerCase()) ||
                ui.dealPageNameSearch == '';

            const filter = ui.dealPageIndustrySearch;
            const industry = dataAccess.value('industry');
            const sub_industry = dataAccess.value('industrySubType');

            let matchIndustry = false;
            if (filter.length == 0) {
                matchIndustry = true;
            } else {
                filter.forEach(filterItem => {
                    if (filterItem.type == 'category') {
                        if (filterItem.value == industry) {
                            matchIndustry = true;
                        }
                    } else if (filterItem.type == 'sub_category') {
                        if (filterItem.value == sub_industry) {
                            matchIndustry = true;
                        }
                    }
                });
            }

            return matchCompanyName && matchIndustry;
        });

        return filtered.length;
    };

    if (!store.isLoaded) {
        return null;
    }

    function filterBy(option, ticker) {
        return (
            option.label.toLowerCase().indexOf(ticker.text.toLowerCase()) > -1
        );
    }

    function filterByReturnAll(option, ticker) {
        return true;
    }

    const braileSpacing = '⠀  ';

    var options_sort = [
        { label: 'Newest first', value: 'newest' },
        { label: 'Oldest first', value: 'oldest' }
    ];

    var options = [];
    const industry = useClientSettingStore().industryTypes;
    industry.forEach(industry => {
        var name = industry.name;
        options.push({
            label: name,
            value: 'CAT:' + name,
            industry: name,
            type: 'category'
        });
        industry.subtypes.forEach(sub_industry => {
            options.push({
                label: braileSpacing + sub_industry.name,
                value: 'SUB:' + sub_industry.name,
                industry: name,
                type: 'subcategory'
            });
        });
    });

    function groupBy(list, keyGetter) {
        const map = {};
        list.forEach(item => {
            const key = keyGetter(item);
            if (!map.hasOwnProperty(key)) {
                map[key] = [];
            }
            map[key].push(item);
        });
        return map;
    }

    var typeahead_props = {};
    typeahead_props['renderMenu'] = (
        results,
        {
            newSelectionPrefix,
            paginationText,
            renderMenuItemChildren,
            ...menuProps
        },
        state
    ) => {
        let index = 0;
        const list = groupBy(results, a => a.industry);
        const items = Object.keys(list)
            .sort()
            .map(region => {
                var subitems = list[region];
                var header = <Menu.Header>{region}</Menu.Header>;
                if (subitems[0].label == region) {
                    header = null;
                }

                return (
                    <Fragment key={region}>
                        {index !== 0 && <Menu.Divider />}
                        {header}
                        {subitems.map(i => {
                            var isSelected = multiSelectionsIndustry.find(
                                a => a.value == i.value
                            );
                            var cls = classNames({
                                [style.selected]: isSelected
                            });
                            const item = (
                                <MenuItem
                                    key={index}
                                    option={i}
                                    position={index}
                                    className={cls}
                                >
                                    <Highlighter search={state.text}>
                                        {i.label}
                                    </Highlighter>
                                </MenuItem>
                            );
                            index += 1;
                            return item;
                        })}
                    </Fragment>
                );
            });

        return <Menu {...menuProps}>{items}</Menu>;
    };

    const filteredDealsCount = getFilteredDealsCount();

    return (
        <Flex direction="column" className={style.header} justify="start">
            <Flex direction="row" className={style.searchBar}>
                <div className={style.leftGroup}>
                    <div className={style.searchWrapper}>
                        <input
                            type="text"
                            className={style.search}
                            value={textSearch}
                            onInput={e => {
                                setTextSearch(e.currentTarget.value);
                                setTextSearchDebounce(e.currentTarget.value);
                            }}
                            placeholder="Search"
                        />
                        <i className={`bi bi-search ${style.searchIcon}`}></i>
                    </div>
                    <div className={style.divider}></div>
                    <div className={style.industrySearch}>
                        <Typeahead
                            {...typeahead_props}
                            id="industry-search"
                            className={style.industrySearch}
                            filterBy={filterBy}
                            options={options}
                            placeholder="Select industry"
                            onChange={updateSelectionIndustry}
                            selected={multiSelectionsIndustry}
                            multiple={true}
                        />
                        <i
                            className={`bi bi-chevron-down ${style.dropdownIcon}`}
                        ></i>
                    </div>
                    <button
                        className={style.resetButton}
                        onClick={resetFilters}
                    >
                        Reset filters
                    </button>
                </div>
                <div className={style.rightGroup}>
                    <div className={style.sortWrapper}>
                        <Typeahead
                            id="sort-select"
                            filterBy={filterByReturnAll}
                            className={style.sortSelect}
                            options={options_sort}
                            placeholder="Newest first"
                            onChange={updateSortSelection}
                            selected={sortSelection}
                        />
                        <i
                            className={`bi bi-chevron-down ${style.dropdownIcon}`}
                        ></i>
                    </div>
                </div>
            </Flex>
            <div className={style.dealCount}>
                {filteredDealsCount}{' '}
                {filteredDealsCount === 1 ? 'case' : 'cases'} shown
            </div>
        </Flex>
    );
});
